import { useLocation } from "react-router-dom";
import "../../../assets/css/AddCatalogs.css";
import "./GeneralTabMenuCSS.css";
import React, { useEffect, useMemo } from "react";
import Dashboard from "../../../components/Atomos/Icons/Dashboard";
import Apartment from "../../../components/Atomos/Icons/Apartment";
import ClinicalNotes from "../../../components/Atomos/Icons/ClinicalNotes";
import Content from "../../../components/Atomos/Icons/Content";
import Communication from "../../../components/Atomos/Icons/Communication";
import GroupsAlt from "../../../components/Atomos/Icons/GroupsAlt";
import SupervisorAccount from "../../../components/Atomos/Icons/SupervisorAccount";
import Gavel from "../../../components/Atomos/Icons/Gavel";
import LocalActivity from "../../../components/Atomos/Icons/LocalActivity";
import { useDispatch, useSelector } from "react-redux";
import { Appstore, manageFirmSlice } from "../../../redux";
import PersistentLink from "../../../components/Moleculas/PersistentLink";
import IFirm from "../../../interfaces/models/IFirm";
import { useStaffMode } from "../../../hooks/StaffModeProvider";
import { isProd } from "../../../Enums/LegacyRedirection";
import {
  isManageFirmLegacyIframePath,
  resolveManageFirmIdForRedux,
} from "../../../hooks/useEnsureManageFirmLoaded";
import { isWsgStaffProfilesFirm, STAFF_PROFILES_LABEL } from "../wsgStaffProfiles";

interface IMenuProps {
  state?: any;
  tabSelected?: string;
}

interface IMenu {
  id: number;
  name: string;
  icon?: (className: string) => React.ReactNode;
  url?: string;
}
const productionDisabledTabIds = new Set([1, 3, 7, 8, 9, 10]);
const getMenuUrl = (tabId: number, url: string) =>
  isProd() && productionDisabledTabIds.has(tabId) ? undefined : url;

const MenuItem: React.FC<{ item: IMenu; selectedTab: number }> = ({
  item,
  selectedTab,
}) => {
  const isSelected = item.id === selectedTab;
  const isDisabled = !item.url;

  return (
    <li
      aria-disabled={isDisabled}
      className={`cursor-pointer menu-item ${isDisabled ? "disabled" : "hover:bg-[#004578]"} ${isSelected ? "selected-item" : ""}`}
    >
      <span>
        {item.icon &&
          item.icon(
            `${isDisabled ? "disabled-icon" : isSelected ? "selected-icon" : "menu-icon"}`
          )}
      </span>
      <span
        className={`${isDisabled ? "disabled-text" : isSelected ? "selected-text" : "menu-text"}`}
      >
        {item.name}
      </span>
    </li>
  );
};

const MenuGT: React.FC<IMenuProps> = () => {
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const manageFirm = useSelector((state: Appstore) => state.manageFirm);
  const extraMenuOpen = useSelector((state: Appstore) => state.menus.extraMenuIsOpen);
  const { isStaffMode } = useStaffMode();

  const firmIdFromUrl = resolveManageFirmIdForRedux(pathname);
  const menuFirm: IFirm | undefined = useMemo(() => {
    if (firmIdFromUrl) {
      if (manageFirm.selectedFirm?.id === firmIdFromUrl) {
        return manageFirm.selectedFirm;
      }
      return { id: firmIdFromUrl } as IFirm;
    }
    return manageFirm.selectedFirm;
  }, [firmIdFromUrl, manageFirm.selectedFirm]);

  const getMenuItems = (selectedFirm: IFirm | undefined): IMenu[] => [
    
    { id: 0, name: "All Firms", url: "/ManageFirm" },
    { id: 1, name: "General", icon: (className) => <Dashboard className={className} />, url: getMenuUrl(1, `/ManageFirm/GeneralTab/${selectedFirm?.id || ''}`) },
    { id: 2, name: "Dashboard", icon: (className) => <Dashboard className={className} />, url: `/ManageFirm/Dashboard/${selectedFirm?.id || ''}` },
    { id: 3, name: "Profile", icon: (className) => <Apartment className={className} />, url: getMenuUrl(3, `/ManageFirm/Profile/${selectedFirm?.id || ''}`) },
    { id: 4, name: isWsgStaffProfilesFirm(selectedFirm?.parentFirm || selectedFirm?.id) ? STAFF_PROFILES_LABEL : "Professionals", icon: (className) => <ClinicalNotes className={className} />, url: `/ManageFirm/Professionals/${(selectedFirm?.parentFirm ? selectedFirm?.parentFirm : selectedFirm?.id) || ''}` },
    { id: 5, name: "Referrals", icon: (className) => <Communication className={className} />, url: getMenuUrl(5, `/ManageFirm/Referrals/${selectedFirm?.id || ''}`) },
    { id: 6, name: "Group Participants", icon: (className) => <GroupsAlt className={className} />, url: `/ManageFirm/GroupParticipants/${selectedFirm?.id || ''}` },
    { id: 7, name: "Practice Heads", icon: (className) => <SupervisorAccount className={className} />, url: getMenuUrl(7, `/ManageFirm/PracticeHeads/${selectedFirm?.id || ''}`) },
    { id: 8, name: "AOP Management", icon: (className) => <Gavel className={className} />, url: getMenuUrl(8, `/ManageFirm/aopManagement/${selectedFirm?.id || ''}`) },
    { id: 9, name: "Events", icon: (className) => <LocalActivity className={className} />, url: getMenuUrl(9, `manage_events.asp?fromadmin=true&pcomp=${selectedFirm?.legacyId || ''}`) },
    { id: 10, name: "Content", icon: (className) => <Content className={className} />, url: getMenuUrl(10, `/ManageFirm/Content/${selectedFirm?.id || ''}`) },
  ];

  const managefirmPaths = [
    'ManageFirm',
    'wsg_admin2.asp', 
    'admin_manage_referrals.asp',
    'wsg_admin15.asp',
    'manage_events.asp'
  ]

  const normalize = (url: string) => url.split(/[?#]/)[0].toLowerCase();

  const getActiveMenuId = (menuItems: IMenu[], pathname: string): number | undefined => {
    const currentPath = normalize(pathname);

    let matchedItem: IMenu | undefined;

    for (const item of menuItems) {
      if (!item.url) {
        continue;
      }
      const base = normalize(item.url);
      if (currentPath === base) {
        return item.id;
      }

      if (currentPath.startsWith(base.endsWith("/") ? base : base + "/")) {
        if (!matchedItem || base.length > normalize(matchedItem?.url ?? '').length) {
          matchedItem = item;
        }
      }
    }

    return matchedItem?.id;
  };


  const isOnManageFirmPath = useMemo(
    () =>
      managefirmPaths.some((path) => {
        const pathRegex = new RegExp(
          `(^|/)${path.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`,
          "i",
        );
        return pathRegex.test(pathname);
      }),
    [pathname],
  );

  const isAllFirmsListPage = useMemo(() => {
    const normalizedPath = (pathname.replace(/\/+$/, "") || "/").toLowerCase();
    return normalizedPath === "/managefirm";
  }, [pathname]);

  const firmIdInPath = useMemo(() => {
    if (firmIdFromUrl) {
      return firmIdFromUrl;
    }
    if (isManageFirmLegacyIframePath(pathname)) {
      const match = pathname.match(
        /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i,
      );
      return match?.[0];
    }
    return undefined;
  }, [pathname, firmIdFromUrl]);

  const hasFirmSelected =
    !isAllFirmsListPage && !!(firmIdInPath || manageFirm.selectedFirm?.id);

  useEffect(() => {
    const showMenu = isStaffMode && isOnManageFirmPath && hasFirmSelected;
    dispatch(manageFirmSlice.actions.SetShowMenu(showMenu));
    const menuItems = getMenuItems(menuFirm);
    const currentTab = getActiveMenuId(menuItems, pathname);

    if (currentTab !== undefined) {
      dispatch(manageFirmSlice.actions.SetTab(currentTab));
    }
  }, [
    pathname,
    menuFirm,
    isStaffMode,
    isOnManageFirmPath,
    hasFirmSelected,
    dispatch,
  ]);

  const renderedMenuItems = useMemo(() => {
    const menuItems = getMenuItems(menuFirm);
    return menuItems.map((item) =>
      item.url ? (
        <PersistentLink
          to={item.url}
          resetOnNavigate
          key={item.id}
        >
          <MenuItem item={item} selectedTab={manageFirm.selectedTab} />
        </PersistentLink>
      ) : (
        <MenuItem
          item={item}
          selectedTab={manageFirm.selectedTab}
          key={item.id}
        />
      )
    );
  }, [menuFirm, manageFirm.selectedTab]);

  return (
    <div className="flex flex-col items-end pointer-events-auto">
      {manageFirm.showMenu && (
        <div className={`sticky ${extraMenuOpen ? 'top-[60px]' : 'top-0'} overflow-x-auto bg-gray-100 border-b-[#E5F3FF] scrollbar-hide z-60 w-full`}>
          <ul className="menu-bar">{renderedMenuItems}</ul>
        </div>
      )}
    </div>
  );
};

export default MenuGT;

